/**
 * 未設定の欄は枠を破線にする（サンプル route-planner: components/dispatch/VehicleInfo.tsx:348, 383）。
 * 実線のままだと「空文字が入っている」のか「まだ選んでいない」のか区別できない。
 */
export function fieldBorderStyle(value: string | null | undefined): 'solid' | 'dashed' {
  return (value ?? '').trim() !== '' ? 'solid' : 'dashed';
}

/**
 * 容量バーの塗り幅。
 * 積載 0 でも 6px の芯を残す（同 :648）。0 幅にすると「バーの無い車両」と
 * 「積載ゼロの車両」が見分けられない。
 */
export function capacityBarWidth(gaugePercent: number): string {
  return `max(6px, ${gaugePercent}%)`;
}

/**
 * 車両カードの高さ。
 *
 * サンプルは 72px だが、それは容量バーがカード外の pb 帯に入り、かつ左端の
 * ツール群（ハンドル・色バー・展開）がカードの外にある前提の値。こちらは
 * ツール群をカード内に入れたうえで 3 段を積む。実ブラウザでの実測は
 * 23 + 2 + 25 + 2 + 20 = 72px で、padding (6 + 10) と合わせて 88px 要る。
 * 76px では 3 段がカードを 12px はみ出し、バーが数値の上に乗っていた。
 *
 * バーの帯 (bottom 3 + 高さ 5) と数値との余白 2px を足した 84px にする。
 * 3 段目の回転追加ボタン (下端 78px) はバーと水平位置が離れるので重ならない。
 *
 * 以下の定数は実ブラウザ (/map・ズーム 100%) での実測値。動かすときは再実測すること。
 */
export const VEHICLE_CARD_HEIGHT = 84;

/**
 * 車両リストのヘッダの高さ。1 段目（フィルタ・車両追加）と 2 段目（全選択）と境界線の合計。
 *
 * レーンの列番号ヘッダもこの高さにする。以前はカード高 (VEHICLE_CARD_HEIGHT) を借りていたため、
 * カードの見た目を直して 76 → 84 に上げたときにレーンヘッダだけが追随し、左の車両行と
 * 右のカード行が 11px ずれた。ヘッダの高さはカードの高さとは別の関心事なので分けて持つ。
 */
export const VEHICLE_LIST_HEADER_ROW1_HEIGHT = 40;
export const VEHICLE_LIST_HEADER_ROW2_HEIGHT = 32;
export const VEHICLE_LIST_HEADER_HEIGHT =
  VEHICLE_LIST_HEADER_ROW1_HEIGHT + VEHICLE_LIST_HEADER_ROW2_HEIGHT + 1;

/**
 * 車両リストの 1 グループ（= 1 勤務）の上下パディング。
 *
 * `VehicleListGroup` の sx が `py: 0.75` で描く値をそのまま持つ（テーマの spacing は 9 なので
 * 6.75px）。数値を別に持つのは、グループの外形高さ（下記）を横並びの画面が使うため。
 * sx に直書きしたままだと、パディングを動かしたときに外形高さだけが古い値のまま残る。
 */
export const VEHICLE_LIST_GROUP_PADDING_Y = 6.75;

/** グループ間の境界線。最終グループには付かない（`VehicleListGroup` の `isLastGroup`） */
export const VEHICLE_LIST_GROUP_DIVIDER_HEIGHT = 1;

/** グループの器がカードの外に足す高さ（上下パディング + 境界線） */
const GROUP_CHROME_HEIGHT =
  VEHICLE_LIST_GROUP_PADDING_Y * 2 + VEHICLE_LIST_GROUP_DIVIDER_HEIGHT;

/**
 * 回転が 1 行だけのグループの外形高さ。
 *
 * 長距離配車（`/long-haul`）は 1 レーン = 1 グループ = 1 行で描き、その横に同じ高さの
 * レーンを並べる。高さが揃っていないと差がそのまま「車両行とレーンの縦ずれ」になるので、
 * 中央ボードの行高はこの値から引く（`src/pages/LongHaul/layout.ts`）。
 */
export const VEHICLE_LIST_SINGLE_ROW_GROUP_HEIGHT =
  VEHICLE_CARD_HEIGHT + GROUP_CHROME_HEIGHT;

/**
 * 外形高さを決めてからカード高を逆算する。行の高さを先に決める画面が使う。
 *
 * 長距離配車は 1 行 116px（サンプルの `MULTIDAY_ROW_HEIGHT`）と外形が先に決まっていて、
 * カードはその中に収まる高さでなければならない。カード高を直接指定させると、器が足す
 * パディングと境界線を呼び出し側が覚えることになり、`VEHICLE_LIST_GROUP_PADDING_Y` を
 * 動かしたときに画面ごとにずれが残る。
 */
export function cardHeightForSingleRowGroup(groupHeight: number): number {
  return groupHeight - GROUP_CHROME_HEIGHT;
}

/** 容量数値の下端。カード上端からの実測。バーはこれより下に置く */
export const VEHICLE_CARD_CONTENT_BOTTOM = 74;

export const CAPACITY_BAR_HEIGHT = 5;

/**
 * 容量バーの位置。カード自身を基準にした絶対配置。
 * - right: トラック幅の基準にだけ使う（実際の右端は capacityBarTrackWidth が決める）
 * - bottom: 容量数値の下端（74px）より下に来る値
 *
 * 左端は画面ごとにツール群の内容が違うので定数では持てない（capacityBarLeftInset）。
 */
export const CAPACITY_BAR_INSET = { right: 60, bottom: 3 } as const;

/** 並び替えハンドルの幅。ハンドルを出さない行でも同じ幅を空ける */
export const DRAG_HANDLE_WIDTH = 16;
/** 車両色バーの幅 */
const COLOR_BAR_WIDTH = 6;
/** 展開トグルの幅 */
const EXPAND_TOGGLE_WIDTH = 20;
/** カード左右の padding（px: 1.5） */
const CARD_PADDING_X = 12;
/** ツール群の負マージン（ml: -0.75）。カードの端まで寄せるぶん本文カラムは左に戻る */
const TOOLS_NEGATIVE_MARGIN = 6;
/** ツール群の中の隙間（gap: 0.25） */
const TOOLS_GAP = 2;
/** ツール群と本文カラムの隙間（gap: 1） */
const TOOLS_CONTENT_GAP = 8;

/**
 * 容量バーの左端＝本文カラムの開始位置。
 *
 * 左のツール群は画面ごとに違う（/map はハンドル＋色バー＋展開トグル、カード配車は
 * ハンドルだけ）。実測の 60px を固定値で持っていたため、カード配車ではバーが数値より
 * 30px 右にずれていた（指摘 2026/08/18）。並びから組み立てて両画面で揃える。
 *
 * /map の組み立て結果が実測値と一致することは vehicleCardStyles.test.ts が固定する。
 */
export function capacityBarLeftInset(
  { hasColorBar, hasExpandToggle }: { hasColorBar: boolean; hasExpandToggle: boolean },
): number {
  const widths = [DRAG_HANDLE_WIDTH];
  if (hasColorBar) widths.push(COLOR_BAR_WIDTH);
  if (hasExpandToggle) widths.push(EXPAND_TOGGLE_WIDTH);
  const tools = widths.reduce((sum, w) => sum + w, 0) + TOOLS_GAP * (widths.length - 1);
  return CARD_PADDING_X - TOOLS_NEGATIVE_MARGIN + tools + TOOLS_CONTENT_GAP;
}

/**
 * バーのトラック幅の上限（左右 inset を除いた残りに対する比率）。
 *
 * 全幅だと「＋回転」ボタンの左端を 5.5px 侵食していた（実測）。数値の桁が増えても
 * ボタンに触れない余地を残すため、比率で絞る（固定 px にすると画面幅で破綻する）。
 */
export const CAPACITY_BAR_MAX_WIDTH_RATIO = 0.75;

export function capacityBarTrackWidth(leftInset: number): string {
  const inset = leftInset + CAPACITY_BAR_INSET.right;
  return `calc((100% - ${inset}px) * ${CAPACITY_BAR_MAX_WIDTH_RATIO})`;
}

/**
 * 車両カードの上下パディング。色バーはこれを負のマージンで打ち消してカード全高に伸ばす
 * （サンプル route-planner の色バーはカードの直下に置かれ、上下いっぱいに出る）。
 */
export const VEHICLE_CARD_PADDING_TOP = 6;
export const VEHICLE_CARD_PADDING_BOTTOM = 10;

/**
 * 車番欄の最小幅。車番は 4〜8 文字と幅が振れるので、内容ぴったりだと
 * 車両ごとに枠の幅が変わって読みにくい。
 */
export const PLATE_NUMBER_MIN_WIDTH = 76;

/**
 * 自動配車で新しく案件が入った車両カードの背景。
 *
 * V3Plans は同じ意味で #EAEFF6 を使うが、あちらの行の地は白。/map の車両カードは
 * 半透明の白が重なった ほぼ白 (実測 rgb(248,250,254)) で、#EAEFF6 では差が
 * 10 程度しかなく見分けられなかったため、停車地行と同じ #D6E3F6 を薄く重ねている。
 *
 * 不透明にしないのは、選択中の行を示す枠 (行の Box の inset shadow) がカードの
 * 下に描かれるため。透かさないと、自動配車の対象車両＝選択中の車両で枠が消える。
 */
export const NEWLY_ASSIGNED_BG = 'rgba(214, 227, 246, 0.6)';

/** 同じ意味で停車地行に敷く色。車両カードと違い、地が白なので不透明のまま出す */
export const NEWLY_ASSIGNED_STOP_BG = '#E4EDFA';

/**
 * ハイライト中の停車地行に hover したときの背景。
 *
 * 停車地行は背景も hover も同じ要素の bgcolor に乗るため、既定の hover 色を当てると
 * hover 中だけハイライトが消える。「どの停車地が新しく入ったか」を確かめようと
 * カーソルを動かすと、まさにその行の情報が失われるので、同系のより濃い青にする。
 */
export const NEWLY_ASSIGNED_STOP_HOVER_BG = '#D2E0F5';
